import { z } from 'zod';
import { uuidSchema, idParamSchema } from '../../common/schemas/id.schemas.js';
import { paginatedSchema, paginationInputSchema, sortInputSchema } from '../../common/schemas/pagination.schemas.js';
import { compareScenarioSchema, compareVehicleIdsSchema, compatibilityVerdictSchema, runSelectionSchema, } from '../../compare/schemas/compare.schemas.js';
/**
 * The executive report (plan §8). A report is an **immutable snapshot**: `reports.save` recomputes the whole
 * payload server-side, freezes it into `reports.snapshot`, and nothing ever edits it again — which is why
 * every schema below carries the rendered string *and* the raw number beside it. The renderer never
 * recomputes and never reformats; a saved report renders identically a year later, after the validity rules
 * that produced its runs have been retuned.
 *
 * A report spans **several tests**. One *test* is one scenario plus the runs selected under it, and a report
 * carries one to six of them over the same two to four vehicles — so a brief can put a mixed-route drive and
 * a stationary idle test side by side, each with its own unit, its own baseline and its own comparability
 * verdict. The document that falls out is `cover · 01 aggregation · one page per test · results`.
 *
 * The report's own text is English by design (plan §11 Phase 5): the builder's UI chrome is translated, the
 * document is a fixed-format executive brief. Section headings and labels therefore live in the snapshot
 * rather than in the web app's locale files.
 */
/** How long a title, a takeaway or a conclusion may be — long enough to edit freely, short enough to print. */
export const REPORT_TITLE_MAX_LENGTH = 200;
export const REPORT_TAKEAWAY_MAX_LENGTH = 600;
export const REPORT_CONCLUSION_MAX_LENGTH = 600;
/** How many conclusions the generator proposes, and the most a builder may end up with. */
export const REPORT_DEFAULT_CONCLUSION_COUNT = 3;
export const REPORT_MAX_CONCLUSIONS = 6;
/**
 * How many tests one report may span. One is the old single-scenario brief; six is where the document stops
 * being a brief — and a page each plus a six-row situation matrix still fit a readable stack of sheets.
 */
export const REPORT_MIN_TESTS = 1;
export const REPORT_MAX_TESTS = 6;
export const reportTitleSchema = z.string().trim().min(1).max(REPORT_TITLE_MAX_LENGTH);
export const reportTakeawaySchema = z.string().trim().min(1).max(REPORT_TAKEAWAY_MAX_LENGTH);
export const reportConclusionTextSchema = z.string().trim().min(1).max(REPORT_CONCLUSION_MAX_LENGTH);
/**
 * A conclusion as the *builder* posts it. Deliberately allows the empty string: the builder's list is
 * dynamic, and emptying a row is how a human removes a conclusion — the server trims every entry and
 * drops the blank ones, so an emptied row genuinely disappears rather than reverting to the generated
 * sentence. What ends up in the snapshot is always non-empty.
 */
export const reportConclusionDraftSchema = z.string().trim().max(REPORT_CONCLUSION_MAX_LENGTH);
export const reportConclusionsInputSchema = z.array(reportConclusionDraftSchema).max(REPORT_MAX_CONCLUSIONS);
/** One vehicle's contribution to a figure: what to print, and the number it was printed from. */
export const reportFigureSchema = z.object({
    vehicleId: uuidSchema,
    /** `null` both for a reading nobody recorded and for a textual one (`95 RON`, `12 August 2026`). */
    value: z.number().nullable(),
    /** `6.8 L`, `21.2`, `25%` — already rounded and unit-suffixed. */
    display: z.string().min(1),
});
/**
 * A figure tile. Page 1 no longer carries any — the cover is a brief without numbers — so the only tiles left
 * in the document are the "why the hybrid used less" ones on the results page.
 */
export const reportStatTileSchema = z.object({
    key: z.string().min(1),
    figure: z.string().min(1),
    label: z.string().min(1),
    caption: z.string().min(1),
    perVehicle: z.array(reportFigureSchema),
});
export const reportSpecRowSchema = z.object({
    label: z.string().min(1),
    value: z.string().min(1),
});
/** A "Vehicles compared" card — one per compared vehicle, two to a row (plan §8). */
export const reportSpecCardSchema = z.object({
    vehicleId: uuidSchema,
    vehicleLabel: z.string().min(1),
    vehicleCode: z.string().min(1),
    /** `HYBRID` / `PETROL` / `DIESEL` / `EV` — the chip printed top-right of the card. */
    badge: z.string().min(1),
    rows: z.array(reportSpecRowSchema),
});
export const reportVehicleColumnSchema = z.object({
    vehicleId: uuidSchema,
    label: z.string().min(1),
    code: z.string().min(1),
    badge: z.string().min(1),
    isBaseline: z.boolean(),
});
/**
 * One cell of the situation matrix: **one vehicle's mean result on one test**, in that test's own unit.
 *
 * The unit rides in the `display` string rather than in a column header, because the unit belongs to the row —
 * a `L/100km` drive and a `L/h` idle test sit one above the other in the same column.
 */
export const reportMatrixCellSchema = z.object({
    vehicleId: uuidSchema,
    /** `21.2 L/100km`, `0.8 L/h`, or an em dash when this vehicle measured nothing on this test. */
    display: z.string().min(1),
    /** The mean the display was printed from; `null` when nothing was measured. */
    raw: z.number().nullable(),
    runCount: z.number().int().min(0),
    /** `(2 runs)` — set only when the cell averages more than one run, so a mean is never read as a single one. */
    runsDisplay: z.string().min(1).nullable(),
    /** The lowest figure in its row — the most efficient, in both units. Every tie on the minimum is marked. */
    isBest: z.boolean(),
});
/**
 * One row of the situation matrix: **one test**, labelled by the scenario factors that actually distinguish it
 * from the report's other tests. Whatever every test shares is lifted into the matrix caption instead, so a
 * six-test brief reads as six situations rather than six repetitions of `Summer · Day · A/C ON`.
 */
export const reportMatrixRowSchema = z.object({
    /** `test-1` — the same key as that test's own page, which is what the `TEST 1` chip cross-references. */
    testKey: z.string().min(1),
    /** `TEST 1` — the chip printed before the label. */
    testLabel: z.string().min(1),
    /** `Mixed · A/C ON · Medium traffic` — the meaningful factors, in a fixed order. */
    label: z.string().min(1),
    /** `Mixed Circuit` / `Stationary test` — the road under the situation, printed muted beneath it. */
    subLabel: z.string().min(1),
    /** This test's unit; two rows of one matrix may legitimately differ. */
    unit: z.string().min(1),
    /** One cell per compared vehicle, in the document's vehicle order. */
    cells: z.array(reportMatrixCellSchema).min(2),
});
/**
 * One vehicle's position on the segment scale. Compared vehicles and same-segment peers share the shape;
 * `isCompared` is what tells the two apart on the page. `value` is the vehicle's **average of its per-test
 * means over every drive test of the report**, which is why no marker names a test.
 */
export const reportScaleMarkerSchema = z.object({
    vehicleId: uuidSchema,
    /** The vehicle code — short enough to print under a tick. */
    label: z.string().min(1),
    value: z.number(),
    display: z.string().min(1),
    /** Where the marker sits between the scale's ends, as a percentage — a plain div offset, never an image. */
    pct: z.number().min(0).max(100),
    isCompared: z.boolean(),
    isBest: z.boolean(),
});
/**
 * The segment position scale: a min→max line with one marker per vehicle, drawn **once** beneath the matrix
 * and belonging to no single test. Each marker is that vehicle's average of its per-test means across every
 * drive test of the report — equal weight per test, idle tests excluded, because a mean of `L/100km` and
 * `L/h` is a number with no meaning. Lower is more efficient, so the left-hand end is the best figure on it.
 */
export const reportPositionScaleSchema = z.object({
    heading: z.string().min(1),
    /**
     * `Average of 2 drive tests — mean L/100km across the Large SUV segment, lower is more efficient`. The
     * count is stated in the snapshot rather than assembled by the renderer, and no scenario code appears in
     * it: the position is deliberately unassigned to any one test.
     */
    caption: z.string().min(1),
    minDisplay: z.string().min(1),
    maxDisplay: z.string().min(1),
    markers: z.array(reportScaleMarkerSchema).min(1),
});
/**
 * The situation matrix — the one table of `01 · TEST AGGREGATION`, and the whole point of the page: one row per
 * test, one column per compared vehicle, and the mean result where they meet. A reader running five or six
 * tests gets every answer in one look, and the per-test pages behind it carry the detail (fuel, distance,
 * conditions, comparability) that would otherwise crowd this table out of readability.
 */
export const reportSituationMatrixSchema = z.object({
    /** `Fuel consumption by situation`. */
    heading: z.string().min(1),
    /**
     * `All tests: Summer · Day` — the scenario dimensions every included test shares, lifted out of the row
     * labels so the rows carry only what differs. `null` when the tests share none of them.
     */
    caption: z.string().min(1).nullable(),
    /** The heading of the label column. */
    measureLabel: z.string().min(1),
    /** Two to four vehicle columns; a four-vehicle brief prints the shorter model name to stay on the sheet. */
    columns: z.array(reportVehicleColumnSchema).min(2).max(4),
    /** One row per included test, in the document's test order. */
    rows: z.array(reportMatrixRowSchema).min(1),
});
/** One definition row of the compact test-conditions block. */
export const reportConditionRowSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    value: z.string().min(1),
});
/**
 * The condensed conditions block: the handful of facts a reader checks before trusting a comparison, closed
 * by the compatibility engine's verdict. It lives on each test's own page, because comparability is judged
 * per test.
 */
export const reportConditionsBlockSchema = z.object({
    heading: z.string().min(1),
    rows: z.array(reportConditionRowSchema),
    verdict: z.object({
        label: z.string().min(1),
        /** `COMPARABLE ✓` — a text glyph, so the line prints without a font or an icon set. */
        display: z.string().min(1),
        verdict: compatibilityVerdictSchema,
    }),
});
/** A row of a side-by-side results block; the economy rows are the emphasised ones. */
export const reportResultRowSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    emphasis: z.boolean(),
    perVehicle: z.array(reportFigureSchema),
});
/** A "why" bar: a percentage rendered as a plain div width, never an image (plan §8). */
export const reportBarSchema = z.object({
    vehicleId: uuidSchema,
    label: z.string().min(1),
    pct: z.number().min(0).max(100),
    display: z.string().min(1),
});
export const reportConclusionSchema = z.object({
    /** `01` / `02` / `03` — renumbered from one whenever the builder adds or removes a row. */
    number: z.string().min(1),
    text: z.string().min(1),
});
/**
 * The brand header, and nothing else: the wordmark row is the whole of page 1's header. There is
 * deliberately no kicker and no dateline — the document names its own subject in the title block, and a
 * standing `FUEL CONSUMPTION ASSESSMENT / Amman — August 2026` block said nothing a reader needed.
 */
export const reportHeaderSchema = z.object({
    /** The text wordmark — no bundled logos anywhere in the document (plan §8). */
    wordmark: z.string().min(1),
});
/**
 * Page 1: the brief, deliberately **without a single number**. Wordmark, title, subtitle, the editable
 * executive takeaway, and a spec card per compared vehicle. The three hero stat tiles are gone — the numbers
 * start on `01 · TEST AGGREGATION`, where they can be read per test and per vehicle rather than as a pair of
 * headline figures.
 */
export const reportPage1Schema = z.object({
    header: reportHeaderSchema,
    title: z.string().min(1),
    subtitle: z.string().min(1),
    takeawayLabel: z.string().min(1),
    takeaway: z.string().min(1),
    specsHeading: z.string().min(1),
    /** Two to four cards, laid out two to a row. */
    specCards: z.array(reportSpecCardSchema).min(2),
});
export const reportSectionHeadingSchema = z.object({
    /** `01` / `02` — the section number printed before the title. */
    number: z.string().min(1),
    title: z.string().min(1),
    subtitle: z.string().min(1),
});
/**
 * Section `01 · TEST AGGREGATION`, the second printed page: **one** situation matrix — a row per test, a column
 * per vehicle — and **one** segment scale beneath it. The per-vehicle rows, the percentages and the fuel and
 * distance columns of the old grouped table are gone: they are on the per-test pages, where a reader who wants
 * a single test's detail is already looking.
 */
export const reportAggregationPageSchema = z.object({
    section: reportSectionHeadingSchema,
    matrix: reportSituationMatrixSchema,
    /**
     * The one segment position scale of the document, averaged over the report's drive tests and therefore
     * assigned to none of them. `null` when the report contains no drive test at all (an idle-only brief) or
     * when no compared vehicle measured a figure on every drive test — the page then prints the matrix alone.
     */
    scale: reportPositionScaleSchema.nullable(),
});
/**
 * One test's economy verdict on the results page: the same vehicles side by side, stripped back to what the
 * money question needs — fuel used, measured economy, the gap to the baseline, and the two readings that
 * explain it.
 */
export const reportEconomyBlockSchema = z.object({
    key: z.string().min(1),
    /** `TEST 1 — Summer · Day · A/C ON · Mixed`. */
    heading: z.string().min(1),
    measureLabel: z.string().min(1),
    columns: z.array(reportVehicleColumnSchema).min(2),
    rows: z.array(reportResultRowSchema).min(1),
});
export const reportResultsPageSchema = z.object({
    section: reportSectionHeadingSchema,
    resultsHeading: z.string().min(1),
    /** One economy block per included test, in the document's test order. */
    blocks: z.array(reportEconomyBlockSchema).min(1),
    /** Only for a hybrid-vs-conventional pairing, and only once — from the primary drive test (plan §8). */
    why: z
        .object({
        heading: z.string().min(1),
        tiles: z.array(reportStatTileSchema),
        barsCaption: z.string().min(1),
        bars: z.array(reportBarSchema),
    })
        .nullable(),
    /**
     * `null` when the builder saved no conclusions at all — heading included, the whole section is then
     * absent from the document rather than printing an empty header (plan §8, post-MVP edit 4).
     */
    conclusions: z
        .object({
        heading: z.string().min(1),
        items: z.array(reportConclusionSchema).min(1).max(REPORT_MAX_CONCLUSIONS),
    })
        .nullable(),
    /**
     * One line of small print, or nothing at all. The only footnote the document still carries is how the
     * fuel was measured, and only when every included run of every test recorded the same method — no
     * protocol version, no "data recorded by", no generated-at stamp and no report id (post-MVP edit 5). The
     * report id stays in the snapshot for traceability; it is simply never printed.
     */
    footer: z
        .object({
        /** `Fuel measured by brim-to-brim refuel`. */
        fuelMethod: z.string().min(1),
    })
        .nullable(),
});
/** A vehicle column of a test page, which says how many runs it averages. */
export const reportTestColumnSchema = reportVehicleColumnSchema.extend({
    /** `1 run` / `3 runs` — printed under the code, so an aggregated column can never be read as one run. */
    runsDisplay: z.string().min(1),
});
/**
 * One reading of a test page's cross-vehicle table. A row exists exactly when **at least one** vehicle
 * recorded the reading; the others print an em dash, because on a side-by-side page the gap is the finding.
 */
export const reportTestReadingRowSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    emphasis: z.boolean(),
    perVehicle: z.array(reportFigureSchema).min(2),
});
/**
 * One included **test** on its own printed page: its route, every vehicle's readings side by side in one
 * table, and the comparability block for that test alone. A vehicle's column aggregates the runs it
 * contributed to this test — a single run is simply that run's own values.
 */
export const reportTestPageSchema = z.object({
    key: z.string().min(1),
    scenarioId: uuidSchema,
    /** `02` · `TEST 1` · `Summer · Day · A/C ON · Mixed` — numbered on from the one aggregation section. */
    section: reportSectionHeadingSchema,
    route: z.object({
        heading: z.string().min(1),
        /**
         * Route name, then code/version/type, then the cumulative ascent-and-descent profile and the traffic the
         * scenario expects — or a single "stationary test — no route" line for an idle test. Each line is
         * present only when there was something to print.
         */
        lines: z.array(z.string().min(1)).min(1),
    }),
    readings: z.object({
        heading: z.string().min(1),
        measureLabel: z.string().min(1),
        columns: z.array(reportTestColumnSchema).min(2),
        rows: z.array(reportTestReadingRowSchema),
    }),
    conditions: reportConditionsBlockSchema,
});
/** One included test, recorded on the snapshot so a frozen report still says what it was built from. */
export const reportSnapshotTestSchema = z.object({
    /** `test-1` — what ties a matrix row, a test page and an economy block together. */
    key: z.string().min(1),
    /** `TEST 1`. */
    label: z.string().min(1),
    scenario: compareScenarioSchema,
    /** `Summer · Day · A/C ON · Mixed`. */
    summary: z.string().min(1),
    unit: z.string().min(1),
    /** Comparability is judged per test, so the verdict lives here as well as on the page. */
    verdict: compatibilityVerdictSchema,
    runIds: z.record(uuidSchema, z.array(uuidSchema)),
});
/**
 * The whole printed document. `reports.get` returns exactly the object `reports.save` computed — every
 * string and every number — so the renderer is a pure function of the snapshot.
 *
 * The pages print in field order: the cover, the aggregation page, one page per included **test**, and the
 * results page last. The printed page count is therefore `2 + testPages.length + 1`.
 */
export const reportSnapshotSchema = z.object({
    /** Kept for traceability even though the document never prints it (post-MVP edit 5). */
    reportId: uuidSchema.nullable(),
    title: z.string().min(1),
    generatedAt: z.string().datetime(),
    /** The **primary** test's scenario — the first one, which the title, subtitle and conclusions speak of. */
    scenario: compareScenarioSchema,
    /** The primary test's unit. Every other unit is on its own test, group and block. */
    unit: z.string().min(1),
    crossSegment: z.boolean(),
    /** The worst verdict across the included tests; each test also carries its own. */
    verdict: compatibilityVerdictSchema,
    vehicleIds: z.array(uuidSchema).min(2),
    /** Every run behind the document, per vehicle, across every test. */
    runIds: z.record(uuidSchema, z.array(uuidSchema)),
    tests: z.array(reportSnapshotTestSchema).min(REPORT_MIN_TESTS).max(REPORT_MAX_TESTS),
    /** Page 1 is fixed at page 1; the pages below move with the test count, so they are named by content. */
    page1: reportPage1Schema,
    aggregation: reportAggregationPageSchema,
    /** One page per included test, in the order the tests were asked for. */
    testPages: z.array(reportTestPageSchema).min(REPORT_MIN_TESTS).max(REPORT_MAX_TESTS),
    results: reportResultsPageSchema,
});
/** The texts the builder may rewrite; everything else is computed and frozen. */
export const reportEditableFieldsSchema = z.object({
    title: reportTitleSchema,
    takeaway: reportTakeawaySchema,
    /** Zero to six. Blank entries are dropped server-side, so clearing a row removes that conclusion. */
    conclusions: reportConclusionsInputSchema,
});
/**
 * One test of a report: a scenario, and which of each vehicle's runs under it to average. The exact shape
 * `compare` takes, minus the vehicles — which are fixed once for the whole report.
 */
export const reportTestInputSchema = z.object({
    scenarioId: uuidSchema,
    runSelection: runSelectionSchema.default('ALL_VALID'),
});
/**
 * One to six tests, each a different scenario. The same scenario twice would produce two identical pages,
 * and the run-picker already covers "some of these runs but not those".
 */
export const reportTestsInputSchema = z
    .array(reportTestInputSchema)
    .min(REPORT_MIN_TESTS, `Include at least ${REPORT_MIN_TESTS} test`)
    .max(REPORT_MAX_TESTS, `Include at most ${REPORT_MAX_TESTS} tests`)
    .refine((tests) => new Set(tests.map((test) => test.scenarioId)).size === tests.length, 'Each scenario can only be included once');
export const generateReportInputSchema = z.object({
    vehicleIds: compareVehicleIdsSchema,
    tests: reportTestsInputSchema,
    /** Overrides the generated title while the builder previews a draft. */
    title: reportTitleSchema.optional(),
});
export const generateReportOutputSchema = z.object({
    snapshot: reportSnapshotSchema,
    /** Returned apart from the snapshot so the builder knows exactly which fields it may edit. */
    editable: reportEditableFieldsSchema,
});
/**
 * What the builder posts. The snapshot is deliberately *not* part of it: the server rebuilds the whole
 * payload from the vehicle ids and the per-test run selections, then applies these three texts, so a
 * tampered or stale client payload can never become a saved report.
 */
export const saveReportInputSchema = z.object({
    vehicleIds: compareVehicleIdsSchema,
    tests: reportTestsInputSchema,
    ...reportEditableFieldsSchema.shape,
});
export const saveReportOutputSchema = z.object({
    id: uuidSchema,
});
/** A row of the reports list. */
export const reportSummarySchema = z.object({
    id: uuidSchema,
    title: z.string().min(1),
    /** The primary test's scenario — what the row is filed under. */
    scenarioId: uuidSchema,
    scenarioCode: z.string().min(1),
    /** How many tests the report spans; `scenarioCode` names the first of them. */
    testCount: z.number().int().min(1),
    vehicleIds: z.array(uuidSchema),
    /** The vehicle labels as they read *in the snapshot*, so a renamed vehicle never rewrites history. */
    vehicleLabels: z.array(z.string().min(1)),
    runCount: z.number().int().min(0),
    createdBy: uuidSchema.nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const reportDetailSchema = reportSummarySchema.extend({
    snapshot: reportSnapshotSchema,
});
export const REPORT_SORT_FIELDS = ['createdAt', 'title'];
export const reportSortBySchema = z.enum(REPORT_SORT_FIELDS);
export const listReportsInputSchema = z.object({
    ...paginationInputSchema.shape,
    ...sortInputSchema.shape,
    sortBy: reportSortBySchema.default('createdAt'),
    sortDirection: z.enum(['asc', 'desc']).default('desc'),
    scenarioId: uuidSchema.optional(),
    vehicleId: uuidSchema.optional(),
});
export const listReportsOutputSchema = paginatedSchema(reportSummarySchema);
export const getReportInputSchema = idParamSchema;
//# sourceMappingURL=reports.schemas.js.map